import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ChevronRight, Copy, MoreHorizontal, Trash2 } from 'lucide-react';
import { PostPanel } from '../components/socialPosts/PostPanel';
import { listTasks, resolveTaskIdentifier, type Task } from '../lib/tasksApi';
import { useTask, useUpdateTask, useDeleteTask } from '../hooks/useTasks';
import { useProject } from '../hooks/useProjects';
import { useBrand } from '../hooks/useCompanyProfile';
import { DuplicateTaskModal } from '../components/tasks/DuplicateTaskModal';
import { CopyTaskLinkButton } from '../components/tasks/CopyTaskLinkButton';
import { ToastProvider, useToast } from '../components/tasks/linear/Toast';
import { Popover } from '../components/tasks/linear/Popover';
import { MarkdownEditor } from '../components/tasks/linear/MarkdownEditor';
import { DetailSidebar, MobileProperties } from '../components/tasks/linear/DetailSidebar';
import { DetailActivity } from '../components/tasks/linear/DetailActivity';
import { SubIssues } from '../components/tasks/linear/SubIssues';
import { TimeTracking } from '../components/tasks/linear/TimeTracking';
import { useI18n } from '../contexts/I18nContext';
import { useConfirm } from '../components/ui/ConfirmProvider';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * CLE-182 — dedicated Linear-style Task Detail page.
 *
 * Two-column dark layout: the left column holds the breadcrumb, an inline
 * editable title, a markdown rich-text description, sub-issues, and the
 * activity + comments timeline; the right column is the ~280px properties
 * sidebar. The route param `:taskIdentifier` may be a UUID (used directly) or
 * a human identifier like "CLE-184" (resolved to a UUID via search, since
 * `GET /api/tasks/:id` resolves by UUID only).
 *
 * CLE-274: reachable via both the canonical project-independent route
 * (`/tasks/:taskIdentifier`) and the legacy project-scoped one
 * (`/projects/:projectId/tasks/:taskIdentifier`). `projectId` is optional; when
 * absent the identifier is resolved across all projects so task links from the
 * dashboard, daily summary and client/project detail never crash.
 */
export function TaskDetailPage() {
  const { projectId, taskIdentifier } = useParams<{ projectId?: string; taskIdentifier: string }>();
  const navigate = useNavigate();

  const isUuid = taskIdentifier ? UUID_RE.test(taskIdentifier) : false;
  const { data: resolved, isLoading: resolving } = useQuery({
    queryKey: ['task-by-identifier', projectId ?? null, taskIdentifier],
    queryFn: async () => {
      // Exact tag first, including one the task has since been renumbered out
      // of: moving a task to another project re-issues its identifier, and a
      // link someone shared under the old tag has to keep working.
      try {
        const hit = await resolveTaskIdentifier(taskIdentifier!);
        // Guarded rather than trusted: Test Mode answers unknown paths with a
        // generic stub, and taking that at face value resolved every task to
        // `undefined` and showed "not found" for all of them.
        if (hit?.id) return { id: hit.id, identifier: hit.identifier };
      } catch {
        // Not a tag at all (someone pasted a title, or it really is gone).
        // Fall back to the old search so those keep resolving as before.
      }
      // projectId may be undefined (canonical /tasks/:id route) — listTasks
      // simply omits the filter and searches across all projects.
      const res = await listTasks({ search: taskIdentifier, projectId, pageSize: 25 });
      const exact = res.tasks.find(
        (t) => t.identifier?.toLowerCase() === taskIdentifier?.toLowerCase(),
      );
      return exact ?? res.tasks[0] ?? null;
    },
    enabled: Boolean(taskIdentifier) && !isUuid,
  });

  const taskId = isUuid ? taskIdentifier : resolved?.id;
  const { data: task, isLoading, isError } = useTask(taskId ?? undefined);
  // Fall back to the resolved task's project when the route is project-agnostic
  // (canonical /tasks/:id), so the breadcrumb still links to the project.
  const effectiveProjectId = projectId ?? task?.projectId;
  const { data: project } = useProject(effectiveProjectId);

  function goBack() {
    // Prefer history-back so the list scroll position (kept by the browser) is
    // restored; fall back to the tasks list.
    if (window.history.length > 1) navigate(-1);
    else navigate('/tasks');
  }

  const stillResolving = !isUuid && Boolean(taskIdentifier) && resolved === undefined;
  if (isLoading || resolving || stillResolving) {
    return <div className="bg-gray-50 p-8 text-sm text-gray-400">Se încarcă sarcina…</div>;
  }
  if (isError || !task || (!isUuid && resolved === null)) {
    return (
      <div className="bg-gray-50 p-8">
        <p className="text-sm text-red-500">Sarcina nu a fost găsită.</p>
        <button type="button" onClick={goBack} className="mt-2 text-sm text-brand-600 underline">
          Înapoi la sarcini
        </button>
      </div>
    );
  }

  return (
    <ToastProvider>
      <div className="flex h-full min-h-0 flex-col bg-gray-50 text-gray-800 lg:flex-row">
        <main className="min-w-0 flex-1 overflow-y-auto px-5 py-6 sm:px-8 lg:px-10">
          <DetailMain
            task={task}
            projectId={effectiveProjectId}
            projectName={project?.name ?? task.projectName}
            projectCode={project?.code ?? null}
            onBack={goBack}
            onDeleted={goBack}
            onNavigateTask={(id) => navigate(`/tasks/${encodeURIComponent(id)}`)}
          />
        </main>
        <DetailSidebar task={task} />
      </div>
    </ToastProvider>
  );
}

function DetailMain({
  task,
  projectId,
  projectName,
  projectCode,
  onBack,
  onDeleted,
  onNavigateTask,
}: {
  task: Task;
  projectId: string | undefined;
  projectName: string | null;
  projectCode: string | null;
  onBack: () => void;
  onDeleted: () => void;
  onNavigateTask: (id: string) => void;
}) {
  const update = useUpdateTask(task.id);
  const del = useDeleteTask();
  const toast = useToast();
  const { t } = useI18n();
  const confirm = useConfirm();
  // First crumb: the instance's brand, same as the sidebar header.
  const { name: brandName } = useBrand();

  const [title, setTitle] = useState(task.title);
  useEffect(() => setTitle(task.title), [task.title]);

  // The title is a one-row textarea that must grow with its text: a long title
  // wraps onto a second line, and with a fixed `rows={1}` that line was simply
  // cut off. Measured after every change and on resize (wrapping depends on the
  // width), the same way the chat composer grows.
  const titleRef = useRef<HTMLTextAreaElement | null>(null);
  useLayoutEffect(() => {
    const fit = () => {
      const el = titleRef.current;
      if (!el) return;
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [title]);
  const [duplicating, setDuplicating] = useState(false);

  async function handleDelete(close: () => void) {
    close();
    if (!(await confirm({ message: t('tasks.row.confirmDelete'), danger: true }))) return;
    del.mutate(task.id, {
      onSuccess: () => {
        toast.show(t('tasks.row.deleted'));
        onDeleted();
      },
      onError: () => toast.show(t('tasks.row.deleteFailed')),
    });
  }

  function commitTitle() {
    const trimmed = title.trim();
    if (!trimmed || trimmed === task.title) {
      setTitle(task.title);
      return;
    }
    update.mutate({ title: trimmed });
  }

  return (
    <div className="group/row mx-auto max-w-3xl">
      {/* Breadcrumb: Company > Project > CLE-XXX, with a trailing actions menu.
          An explicit "back" leads it (Andrei, 2026-09-29): the crumb alone read
          as a label, and people looked for a way back to the list. */}
      <nav className="flex items-center gap-1 text-[13px] text-gray-500">
        <button
          type="button"
          onClick={onBack}
          aria-label="Înapoi"
          title="Înapoi"
          className="-ml-1.5 mr-1 inline-flex min-h-8 items-center gap-1 rounded-md px-1.5 text-gray-500 hover:bg-gray-200 hover:text-gray-800"
        >
          <ArrowLeft size={15} />
          <span>Înapoi</span>
        </button>
        {/* On a phone the crumb keeps one line: no company step (the back
            button covers it), the project name truncates, the tag never
            breaks ("PERS-" / "1"). */}
        <button
          type="button"
          onClick={onBack}
          title={brandName}
          className="hidden max-w-[12rem] shrink-0 truncate whitespace-nowrap hover:text-gray-800 sm:inline-block"
        >
          {brandName}
        </button>
        <ChevronRight size={14} className="hidden shrink-0 text-gray-300 sm:block" />
        {projectId ? (
          <Link to={`/projects/${projectId}`} className="min-w-0 truncate hover:text-gray-800">
            {projectName ?? projectCode ?? 'Proiect'}
          </Link>
        ) : (
          <span className="min-w-0 truncate">{projectName ?? projectCode ?? 'Proiect'}</span>
        )}
        <ChevronRight size={14} className="shrink-0 text-gray-300" />
        <span className="shrink-0 whitespace-nowrap font-mono text-gray-700">
          {task.identifier ?? 'LOCAL'}
        </span>

        <div className="ml-auto flex shrink-0 items-center gap-1">
          {/* Copy the canonical link (the tag, not the uuid) so what lands in
              someone's chat reads as the ticket it points at. */}
          <CopyTaskLinkButton
            task={task}
            onCopied={() => toast.show(t('tasks.share.copied'))}
            onFailed={() => toast.show(t('tasks.share.copyFailed'))}
          />

          <Popover
            width={160}
            align="end"
            trigger={({ open, toggle, ref }) => (
              <button
                type="button"
                ref={ref}
                onClick={toggle}
                aria-label={t('tasks.row.menu')}
                className={`flex h-7 w-7 items-center justify-center rounded text-gray-500 hover:bg-gray-200 hover:text-gray-800 ${
                  open ? 'bg-gray-200 text-gray-800' : ''
                }`}
              >
                <MoreHorizontal size={16} />
              </button>
            )}
          >
            {(close) => (
              <div className="py-1">
                <button
                  type="button"
                  onClick={() => {
                    close();
                    setDuplicating(true);
                  }}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] text-gray-700 hover:bg-gray-200"
                >
                  <Copy size={13} /> {t('tasks.row.duplicate')}
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(close)}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] text-red-500 hover:bg-gray-200"
                >
                  <Trash2 size={13} /> {t('tasks.row.delete')}
                </button>
              </div>
            )}
          </Popover>
        </div>
      </nav>

      {duplicating && (
        <DuplicateTaskModal
          task={task}
          onClose={() => setDuplicating(false)}
          onDuplicated={(copy) => {
            setDuplicating(false);
            toast.show(t('tasks.duplicate.done', { identifier: copy.identifier ?? copy.title }));
            onNavigateTask(copy.identifier ?? copy.id);
          }}
        />
      )}

      {/* Editable title */}
      <textarea
        ref={titleRef}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onBlur={commitTitle}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            (e.target as HTMLTextAreaElement).blur();
          }
          if (e.key === 'Escape') {
            setTitle(task.title);
            (e.target as HTMLTextAreaElement).blur();
          }
        }}
        rows={1}
        className="mt-4 w-full resize-none bg-transparent text-[22px] font-semibold leading-tight text-gray-900 placeholder:text-gray-400 focus:outline-none"
        placeholder="Titlul sarcinii"
      />

      {/* Compact Linear-style property chips — phones only. Below lg the
          280px sidebar is hidden (it used to stack at the bottom and crush
          the description to a sliver), so the properties live up here. */}
      <div className="mt-3 lg:hidden">
        <MobileProperties task={task} />
      </div>

      {/* Description (markdown rich-text editor) */}
      <div className="mt-2">
        <MarkdownEditor
          taskId={task.id}
          value={task.description ?? ''}
          isSaving={update.isPending}
          onSave={(description) => update.mutate({ description })}
        />
      </div>

      {/* Calendar de postări: the publishing side, when this task is a post. */}
      {task.socialPost && <PostPanel taskId={task.id} />}

      {/* Sub-issues */}
      <SubIssues taskId={task.id} parentProjectId={task.projectId} onOpenTask={onNavigateTask} />

      {/* Time tracking (worklog) */}
      <TimeTracking taskId={task.id} estimate={task.estimate} />

      {/* Activity timeline + comments */}
      <DetailActivity taskId={task.id} />
    </div>
  );
}
